import { ReactNode } from "react";
import { useAuth, type AppRole } from "@/hooks/useAuth";

interface RoleGuardProps {
  allowedRoles: AppRole[];
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Conditionally renders children only when the current user
 * holds at least one of the specified roles.
 *
 * Usage:
 *   <RoleGuard allowedRoles={["admin"]}>
 *     <AdminOnlyButton />
 *   </RoleGuard>
 */
export default function RoleGuard({ allowedRoles, fallback = null, children }: RoleGuardProps) {
  const { roles, loading } = useAuth();

  if (loading) return null;

  const hasAccess = allowedRoles.some((role) => roles.includes(role));

  return hasAccess ? <>{children}</> : <>{fallback}</>;
}
